
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Token } from '../../../types';
import { Card } from '../../ui/Card';
import { ExternalLink, TrendingUp, TrendingDown, Info } from 'lucide-react';
import { AreaChart } from '../../charts/AreaChart';

interface TokenDetailsProps {
    fromToken: Token | null;
    toToken: Token | null;
}

export const TokenDetails: React.FC<TokenDetailsProps> = ({ fromToken, toToken }) => {
    const focusToken = toToken || fromToken;
    if (!focusToken) return null;

    // Simulated 24h market chart data
    const chartData = [45, 48, 47, 52, 50, 58, 62, 59, 65, 70, 68, 72];

    return (
        <Card className="border-stone-200 dark:border-[#2D3142] p-6 sm:p-7 overflow-hidden transition-colors">
            <div className="flex items-center justify-between gap-4 mb-8">
                <div className="flex items-center gap-3">
                    <div className="relative shrink-0">
                        <img src={focusToken.icon} className="w-9 h-9 rounded-full border border-stone-100 dark:border-white/10 shadow-sm" alt={focusToken.name} />
                        <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-[#15171C] ${focusToken.chain === 'SOL' ? 'bg-purple-500' : 'bg-blue-500'}`} />
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="font-serif text-lg font-bold text-[#0A0B0F] dark:text-white leading-tight">Market Data</h3>
                            <a 
                                href={focusToken.chain === 'SOL' ? `https://solscan.io/token/${focusToken.mint}` : `https://suiscan.org/mainnet/coin/${focusToken.mint}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-stone-400 hover:text-nobel-gold transition-colors"
                            >
                                <ExternalLink size={12} />
                            </a>
                        </div>
                        <div className="text-[10px] font-mono text-stone-400 uppercase tracking-tighter mt-0.5">{focusToken.symbol} / USD</div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-xl sm:text-2xl font-mono font-bold text-[#0A0B0F] dark:text-white tracking-tighter">
                        ${focusToken.priceUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                    <div className={`flex items-center justify-end gap-1 text-[10px] font-black ${focusToken.change24h >= 0 ? 'text-emerald-500' : 'text-red-500'} uppercase tracking-widest`}>
                        {focusToken.change24h >= 0 ? <TrendingUp size={12}/> : <TrendingDown size={12}/>}
                        {focusToken.change24h.toFixed(2)}%
                    </div>
                </div>
            </div>

            <div className="h-32 w-full mb-8 relative">
                <AreaChart data={chartData} className="opacity-80" />
            </div>

            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-stone-100 dark:border-white/5">
                <div>
                    <div className="text-[9px] text-stone-400 font-bold uppercase tracking-[0.2em] mb-1">Market Cap</div>
                    <div className="text-xs font-mono font-bold text-[#0A0B0F] dark:text-white">$2.44B</div>
                </div>
                <div>
                    <div className="text-[9px] text-stone-400 font-bold uppercase tracking-[0.2em] mb-1">24h Volume</div>
                    <div className="text-xs font-mono font-bold text-[#0A0B0F] dark:text-white">$142.5M</div>
                </div>
                <div className="col-span-2">
                    <div className="text-[9px] text-stone-400 font-bold uppercase tracking-[0.2em] mb-1">On-Chain Identifier</div>
                    <div className="text-[10px] font-mono text-stone-500 bg-stone-50 dark:bg-white/5 p-2 rounded-lg truncate border border-stone-100 dark:border-white/5">
                        {focusToken.mint}
                    </div>
                </div>
            </div>
            
            <div className="mt-8 p-3.5 bg-nobel-gold/5 dark:bg-white/[0.02] rounded-xl flex items-start gap-3 border border-nobel-gold/10">
                <Info size={14} className="text-nobel-gold mt-0.5 shrink-0" />
                <p className="text-[9px] text-stone-500 dark:text-stone-400 leading-relaxed font-medium uppercase tracking-wide">
                    Prices aggregated via <span className="text-nobel-gold font-bold">Pyth Oracle</span> across 14 independent validators for sub-second accuracy.
                </p>
            </div>
        </Card>
    );
};
